import { Award, Calendar, Building2, Link2, Share2, ExternalLink, QrCode } from 'lucide-react';
import type { PatientCertificateDoc } from '../types';
import NeoVerifyBadge from './NeoVerifyBadge';

const TYPE_COLORS: Record<string, string> = {
  VACCINATION_CERTIFICATE: 'bg-blue-50 text-blue-700 border-blue-200',
  MEDICAL_FITNESS_CERTIFICATE: 'bg-green-50 text-green-700 border-green-200',
  SICK_LEAVE_CERTIFICATE: 'bg-amber-50 text-amber-700 border-amber-200',
  DISCHARGE_SUMMARY_CERTIFICATE: 'bg-purple-50 text-purple-700 border-purple-200',
  LAB_REPORT_CERTIFICATE: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  PRESCRIPTION_CERTIFICATE: 'bg-teal-50 text-teal-700 border-teal-200',
  BLOOD_DONATION_CERTIFICATE: 'bg-red-50 text-red-700 border-red-200',
  INSURANCE_CLAIM_CERTIFICATE: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  SURGERY_PROCEDURE_CERTIFICATE: 'bg-rose-50 text-rose-700 border-rose-200',
  AMBULANCE_TRANSPORT_CERTIFICATE: 'bg-orange-50 text-orange-700 border-orange-200',
  EMERGENCY_EVENT_CERTIFICATE: 'bg-red-50 text-red-700 border-red-200',
};

function fmtDate(d?: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtType(t: string) {
  return t.replace(/_CERTIFICATE$/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export default function CertificateCard({
  cert, onShare, onView,
}: {
  cert: PatientCertificateDoc;
  onShare?: (cert: PatientCertificateDoc) => void;
  onView?: (cert: PatientCertificateDoc) => void;
}) {
  const color = TYPE_COLORS[cert.certificateType] || 'bg-gray-50 text-gray-700 border-gray-200';
  const isExpired = cert.status === 'EXPIRED' || (cert.expiryDate && new Date(cert.expiryDate) < new Date());
  const isRevoked = cert.status === 'REVOKED';

  return (
    <div className={`card p-4 hover:shadow-md transition-shadow ${isExpired || isRevoked ? 'opacity-75' : ''}`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${color}`}>
          <Award className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 truncate">{cert.title}</h3>
              <span className={`inline-block mt-1 text-[10px] font-medium px-2 py-0.5 rounded border ${color}`}>
                {fmtType(cert.certificateType)}
              </span>
            </div>
            <NeoVerifyBadge status={cert.verificationStatus} neoVerifyId={cert.neoVerifyId} />
          </div>

          {cert.description && (
            <p className="text-xs text-gray-500 mt-2 line-clamp-2">{cert.description}</p>
          )}

          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[11px] text-gray-500">
            {cert.issuerName && (
              <span className="flex items-center gap-1">
                <Building2 className="w-3 h-3" /> {cert.issuerName}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" /> {fmtDate(cert.issueDate)}
            </span>
            {cert.expiryDate && (
              <span className={`flex items-center gap-1 ${isExpired ? 'text-red-500 font-semibold' : ''}`}>
                Exp: {fmtDate(cert.expiryDate)}
              </span>
            )}
            {/* Who issued it, which the record really carries. `linkedModule`
                was read here and is not a field this API sends. */}
            {cert.issuerName && (
              <span className="flex items-center gap-1">
                <Link2 className="w-3 h-3" /> {cert.issuerName}
              </span>
            )}
          </div>

          {/* Status badges */}
          <div className="flex items-center gap-2 mt-2">
            {isExpired && <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-red-50 text-red-600 border border-red-200">Expired</span>}
            {isRevoked && <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-red-50 text-red-600 border border-red-200">Revoked</span>}
            {cert.neoVerifyId && (
              <span className="text-[10px] text-gray-400 flex items-center gap-0.5" title={cert.neoVerifyId}>
                <QrCode className="w-3 h-3" /> QR
              </span>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-100">
            {onView && (
              <button onClick={() => onView(cert)} className="text-[11px] text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1">
                <ExternalLink className="w-3 h-3" /> View
              </button>
            )}
            {onShare && cert.isShareable && !isRevoked && (
              <button onClick={() => onShare(cert)} className="text-[11px] text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                <Share2 className="w-3 h-3" /> Share
              </button>
            )}
            {/* The issuer's own reference — what a person quotes on a phone
                call. This used to print a `verifyUrl` no endpoint has ever
                sent, so the line was blank on every card. */}
            {cert.certificateNumber && (
              <span className="text-[11px] text-gray-400 ml-auto truncate max-w-[150px] font-mono" title={cert.certificateNumber}>
                {cert.certificateNumber}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
